import { useState } from "react"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { Tag, TagLabel } from "~/react/ui/tag"
import { BarChart, ChartContainer, type BarChartDataset, type ChartConfig } from "~/react/ui/chart"
import type { EmailAccount } from "~/react/components/email_accounts/email_accounts_filters"
import { getWarmupStateFromProtection, type WarmupState } from "~/react/components/email_accounts/utils"
import { InboxProtectionModal } from "~/react/components/email_accounts_center/inbox_protection_modal"
import type {
  BillingInfo,
  CreditCardInfo,
  PricePerWarmup,
} from "~/react/components/email_accounts_center/add_email_account_slots_modal/constants"

type WarmupTabProps = {
  account: EmailAccount
  stripePublicKey?: string | null
  creditCardInfo?: CreditCardInfo | null
  cardholderName?: string | null
  billingInfo?: BillingInfo | null
  userEmail?: string | null
  pricePerWarmup: PricePerWarmup
}

type WarmupChartPoint = {
  date: string
  sent: number
  scheduled: number
}

type WarmupNotConfiguredProps = {
  onLearnMore: (event: React.MouseEvent<HTMLButtonElement>) => void
  emailAccountId?: number | null
  stripePublicKey?: string | null
  creditCardInfo?: CreditCardInfo | null
  cardholderName?: string | null
  billingInfo?: BillingInfo | null
  userEmail?: string | null
  pricePerWarmup: PricePerWarmup
}

type WarmupConfiguredProps = {
  isInactive: boolean
  isWarmingUp: boolean
  statusVariant: "warning" | "info"
  statusLabel: string
  progressPercent: number
  progressEndDate: string
  currentLimit: number
  targetLimit: number
  chartData: WarmupChartPoint[]
  chartMaxValue: number
  onLearnMore: (event: React.MouseEvent<HTMLButtonElement>) => void
}

const getWarmupState = (account: EmailAccount): WarmupState => getWarmupStateFromProtection(account.protection)

const getWarmupChartColors = () => {
  const root = document.documentElement
  const getVar = (name: string) => getComputedStyle(root).getPropertyValue(name).trim()

  return {
    sent: getVar("--colors-secondary-600"),
    scheduled: getVar("--colors-grey-300"),
  }
}

const warmupChartData: WarmupChartPoint[] = [
  { date: "Jan 01", sent: 1, scheduled: 2 },
  { date: "Jan 02", sent: 1, scheduled: 3 },
  { date: "Jan 03", sent: 2, scheduled: 3 },
  { date: "Jan 04", sent: 2, scheduled: 4 },
  { date: "Jan 05", sent: 2, scheduled: 5 },
  { date: "Jan 06", sent: 3, scheduled: 5 },
  { date: "Jan 07", sent: 3, scheduled: 6 },
  { date: "Jan 08", sent: 3, scheduled: 7 },
  { date: "Jan 09", sent: 4, scheduled: 7 },
  { date: "Jan 10", sent: 4, scheduled: 8 },
  { date: "Jan 11", sent: 4, scheduled: 8 },
  { date: "Jan 12", sent: 5, scheduled: 9 },
  { date: "Jan 13", sent: 5, scheduled: 10 },
  { date: "Jan 14", sent: 5, scheduled: 11 },
  { date: "Jan 15", sent: 6, scheduled: 11 },
  { date: "Jan 16", sent: 6, scheduled: 12 },
  { date: "Jan 17", sent: 6, scheduled: 13 },
  { date: "Jan 18", sent: 7, scheduled: 13 },
  { date: "Jan 19", sent: 7, scheduled: 14 },
  { date: "Jan 20", sent: 7, scheduled: 15 },
  { date: "Jan 21", sent: 8, scheduled: 15 },
  { date: "Jan 22", sent: 8, scheduled: 16 },
  { date: "Jan 23", sent: 8, scheduled: 17 },
  { date: "Jan 24", sent: 9, scheduled: 17 },
  { date: "Jan 25", sent: 9, scheduled: 18 },
  { date: "Jan 26", sent: 9, scheduled: 19 },
  { date: "Jan 27", sent: 10, scheduled: 19 },
  { date: "Jan 28", sent: 10, scheduled: 20 },
  { date: "Jan 29", sent: 10, scheduled: 21 },
  { date: "Jan 30", sent: 11, scheduled: 21 },
  { date: "Jan 31", sent: 11, scheduled: 22 },
  { date: "Feb 01", sent: 11, scheduled: 23 },
  { date: "Feb 02", sent: 12, scheduled: 23 },
  { date: "Feb 03", sent: 12, scheduled: 24 },
  { date: "Feb 04", sent: 12, scheduled: 25 },
  { date: "Feb 05", sent: 13, scheduled: 25 },
  { date: "Feb 06", sent: 13, scheduled: 26 },
  { date: "Feb 07", sent: 13, scheduled: 27 },
  { date: "Feb 08", sent: 14, scheduled: 27 },
]

const WarmupNotConfigured = ({
  onLearnMore,
  emailAccountId,
  stripePublicKey,
  creditCardInfo,
  cardholderName,
  billingInfo,
  userEmail,
  pricePerWarmup,
}: WarmupNotConfiguredProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const emailAccountIds = emailAccountId ? [emailAccountId] : []

  return (
    <div className="email-account-warmup d-flex flex-column gap-3">
      <h3 className="email-account-warmup__not-configured-title m-0">
        {i18n.t("js.email_accounts_center.email_account.warmup.not_configured.title")}
      </h3>
      <p className="email-account-warmup__not-configured-description mb-4">
        {i18n.t("js.email_accounts_center.email_account.warmup.not_configured.description")}
      </p>
      <ul className="email-account-warmup__not-configured-list list-unstyled m-0">
        <li className="email-account-warmup__not-configured-item">
          <Icon icon="check" size="sm" className="email-account-warmup__not-configured-icon" />
          <span>{i18n.t("js.email_accounts_center.email_account.warmup.not_configured.benefits.reduce_spam")}</span>
        </li>
        <li className="email-account-warmup__not-configured-item">
          <Icon icon="check" size="sm" className="email-account-warmup__not-configured-icon" />
          <span>
            {i18n.t("js.email_accounts_center.email_account.warmup.not_configured.benefits.background_sequences")}
          </span>
        </li>
        <li className="email-account-warmup__not-configured-item">
          <Icon icon="check" size="sm" className="email-account-warmup__not-configured-icon" />
          <span>
            {i18n.t("js.email_accounts_center.email_account.warmup.not_configured.benefits.long_term_outreach")}
          </span>
        </li>
        <li className="email-account-warmup__not-configured-item">
          <Icon icon="check" size="sm" className="email-account-warmup__not-configured-icon" />
          <span>{i18n.t("js.email_accounts_center.email_account.warmup.not_configured.benefits.no_setup")}</span>
        </li>
      </ul>
      <Button
        variant="primary"
        size="sm"
        type="button"
        fullWidth
        className="email-account-warmup__not-configured-cta"
        onClick={() => setIsModalOpen(true)}
      >
        {i18n.t("js.email_accounts_center.email_account.warmup.not_configured.cta")}
      </Button>
      <InboxProtectionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onLearnMore={onLearnMore}
        emailAccountIDs={emailAccountIds}
        stripePublicKey={stripePublicKey}
        creditCardInfo={creditCardInfo}
        cardholderName={cardholderName}
        billingInfo={billingInfo}
        userEmail={userEmail}
        pricePerWarmup={pricePerWarmup}
      />
    </div>
  )
}

WarmupNotConfigured.displayName = "WarmupNotConfigured"

const WarmupConfigured = ({
  isInactive,
  isWarmingUp,
  statusVariant,
  statusLabel,
  progressPercent,
  progressEndDate,
  currentLimit,
  targetLimit,
  chartData,
  chartMaxValue,
  onLearnMore,
}: WarmupConfiguredProps) => {
  const [showDetails, setShowDetails] = useState(false)
  const colors = getWarmupChartColors()
  const chartConfig: ChartConfig = {
    sent: {
      label: i18n.t("js.email_accounts_center.email_account.warmup.activity.chart_legend_sent"),
      color: colors.sent,
    },
    scheduled: {
      label: i18n.t("js.email_accounts_center.email_account.warmup.activity.chart_legend_scheduled"),
      color: colors.scheduled,
    },
  }
  const datasets: BarChartDataset[] = [
    {
      dataKey: "sent",
      stackId: "warmup",
      fill: colors.sent,
      barThickness: 18,
      maxBarThickness: 22,
      categoryPercentage: 0.65,
      barPercentage: 0.8,
      radius: { topLeft: 2, topRight: 2, bottomLeft: 0, bottomRight: 0 },
      order: 1,
    },
    {
      dataKey: "scheduled",
      stackId: "warmup",
      fill: colors.scheduled,
      barThickness: 18,
      maxBarThickness: 22,
      categoryPercentage: 0.65,
      barPercentage: 0.8,
      radius: { topLeft: 2, topRight: 2, bottomLeft: 0, bottomRight: 0 },
      order: 2,
    },
  ]
  const chartPoints = chartData.map((point) => ({
    date: point.date,
    sent: point.sent,
    scheduled: point.scheduled,
  }))
  const chartWidthRem = Math.max(chartPoints.length * 2.4, 60)

  return (
    <div className="email-account-warmup d-flex flex-column gap-3">
      <div className="email-account-warmup__header d-flex align-items-center justify-content-between gap-2">
        <Tag variant={statusVariant} size="sm">
          <TagLabel>{statusLabel}</TagLabel>
        </Tag>
        <Button
          variant="link"
          size="sm"
          type="button"
          className="email-account-warmup__learn-link"
          onClick={onLearnMore}
        >
          {i18n.t("js.email_accounts_center.email_account.warmup.warming_up.link")}
        </Button>
      </div>

      <div className="email-account-warmup__intro">
        <h3 className="email-account-warmup__title m-0">
          {isInactive
            ? i18n.t("js.email_accounts_center.email_account.warmup.inactive.title")
            : i18n.t("js.email_accounts_center.email_account.warmup.warming_up.title")}
        </h3>
        <p className="email-account-warmup__description mb-0">
          {isInactive
            ? i18n.t("js.email_accounts_center.email_account.warmup.inactive.description")
            : i18n.t("js.email_accounts_center.email_account.warmup.warming_up.description")}
        </p>
      </div>

      {isWarmingUp && (
        <div className="email-account-warmup__progress">
          <div className="email-account-warmup__progress-header">
            <span className="email-account-warmup__progress-label">
              {i18n.t("js.email_accounts_center.email_account.warmup.progress.label", {
                percent: `${progressPercent}%`,
              })}
            </span>
            <span className="email-account-warmup__progress-end">
              {i18n.t("js.email_accounts_center.email_account.warmup.progress.end_on", { date: progressEndDate })}
            </span>
          </div>
          <div className="email-account-warmup__progress-bar">
            <div
              className="email-account-warmup__progress-fill"
              style={{ "--progress": `${progressPercent}%` } as any}
            />
          </div>
          <div className="email-account-warmup__progress-footer">
            <span>
              {i18n.t("js.email_accounts_center.email_account.warmup.progress.current_limit", { limit: currentLimit })}
            </span>
            <span>
              {i18n.t("js.email_accounts_center.email_account.warmup.progress.target_limit", { limit: targetLimit })}
            </span>
          </div>
        </div>
      )}

      <div className="email-account-warmup__divider" />

      <div className="email-account-warmup__activity d-flex flex-column">
        <h4 className="email-account-warmup__section-title">
          {i18n.t("js.email_accounts_center.email_account.warmup.activity.title")}
        </h4>
        <p className="email-account-warmup__section-description">
          {i18n.t("js.email_accounts_center.email_account.warmup.activity.description")}
        </p>

        <div className={`email-account-warmup__stats ${isInactive ? "is-inactive" : ""}`}>
          <div className="email-account-warmup__stat">
            <Icon icon="envelope" size="sm" />
            <div className="email-account-warmup__stat-body">
              <div className="email-account-warmup__stat-label">
                {i18n.t("js.email_accounts_center.email_account.warmup.activity.emails_sent")}
              </div>
              <div className="email-account-warmup__stat-value">118</div>
            </div>
          </div>
          <div className="email-account-warmup__stat">
            <Icon icon="handshake" size="sm" />
            <div className="email-account-warmup__stat-body">
              <div className="email-account-warmup__stat-label">
                {i18n.t("js.email_accounts_center.email_account.warmup.activity.interactions")}
              </div>
              <div className="email-account-warmup__stat-value">37</div>
            </div>
          </div>
          <div className="email-account-warmup__stat">
            <Icon icon="exclamation-triangle" size="sm" />
            <div className="email-account-warmup__stat-body">
              <div className="email-account-warmup__stat-label">
                {i18n.t("js.email_accounts_center.email_account.warmup.activity.in_spam")}
              </div>
              <div className="email-account-warmup__stat-value">7</div>
            </div>
          </div>
        </div>

        <Button
          variant="link"
          size="sm"
          type="button"
          className="email-account-warmup__details-link"
          onClick={() => setShowDetails((current) => !current)}
        >
          {i18n.t(
            showDetails
              ? "js.email_accounts_center.email_account.warmup.activity.hide_details"
              : "js.email_accounts_center.email_account.warmup.activity.show_details",
          )}
        </Button>

        {showDetails && (
          <div className={`email-account-warmup__history position-relative ${isInactive ? "is-inactive" : ""}`}>
            <div className="email-account-warmup__chart d-flex flex-column">
              <div className="email-account-warmup__chart-scroll">
                <div className="email-account-warmup__chart-inner" style={{ width: `${chartWidthRem}rem` }}>
                  <ChartContainer config={chartConfig} className="email-account-warmup__chart-container">
                    <BarChart
                      data={chartPoints}
                      xAxisKey="date"
                      datasets={datasets}
                      stacked
                      yAxisMax={chartMaxValue}
                      showTooltip
                    />
                  </ChartContainer>
                </div>
              </div>
              <div className="d-flex align-items-center gap-3 mt-3 text-muted small">
                <span className="d-inline-flex align-items-center gap-2">
                  <span className="email-account-warmup__legend-dot email-account-warmup__legend-dot--sent" />
                  {i18n.t("js.email_accounts_center.email_account.warmup.activity.chart_legend_sent")}
                </span>
                <span className="d-inline-flex align-items-center gap-2">
                  <span className="email-account-warmup__legend-dot email-account-warmup__legend-dot--scheduled" />
                  {i18n.t("js.email_accounts_center.email_account.warmup.activity.chart_legend_scheduled")}
                </span>
                {!isInactive && (
                  <span className="ms-auto">
                    {i18n.t("js.email_accounts_center.email_account.warmup.activity.warmup_end", {
                      date: "Feb. 27, 2026",
                    })}
                  </span>
                )}
              </div>
            </div>

            {isInactive && (
              <div className="email-account-warmup__history-overlay">
                <h4 className="fs-6 fw-semibold mb-1">
                  {i18n.t("js.email_accounts_center.email_account.warmup.activity.historical_title")}
                </h4>
                <p className="text-muted mb-2">
                  {i18n.t("js.email_accounts_center.email_account.warmup.activity.historical_description")}
                </p>
                <Button size="sm" type="button">
                  {i18n.t("js.email_accounts_center.email_account.warmup.inactive.cta")}
                </Button>
              </div>
            )}
          </div>
        )}
        {!isInactive && (
          <Button size="sm" type="button" className="align-self-start mt-3">
            {i18n.t("js.email_accounts_center.email_account.warmup.actions.disable")}
          </Button>
        )}
      </div>
    </div>
  )
}

WarmupConfigured.displayName = "WarmupConfigured"

export default function WarmupTab({
  account,
  stripePublicKey,
  creditCardInfo,
  cardholderName,
  billingInfo,
  userEmail,
  pricePerWarmup,
}: WarmupTabProps) {
  // TODO: Replace placeholder data and state with backend warmup data.
  const warmupState = getWarmupState(account)
  const chartMaxValue = Math.max(...warmupChartData.map((point) => point.sent + point.scheduled), 1)
  const handleLearnMore = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  if (warmupState === "not_configured") {
    return (
      <WarmupNotConfigured
        onLearnMore={handleLearnMore}
        emailAccountId={account.id}
        stripePublicKey={stripePublicKey}
        creditCardInfo={creditCardInfo}
        cardholderName={cardholderName}
        billingInfo={billingInfo}
        userEmail={userEmail}
        pricePerWarmup={pricePerWarmup}
      />
    )
  }

  const isInactive = warmupState === "inactive"
  const isWarmingUp = warmupState === "warming_up"
  const statusVariant = isInactive ? "warning" : "info"
  const statusLabel = isInactive
    ? i18n.t("js.email_accounts_center.email_account.warmup.status.at_risk")
    : i18n.t("js.email_accounts_center.email_account.warmup.status.warming_up")
  const progressPercent = 82
  const progressEndDate = "Feb. 27, 2026"
  const currentLimit = 24
  const targetLimit = 45

  return (
    <WarmupConfigured
      isInactive={isInactive}
      isWarmingUp={isWarmingUp}
      statusVariant={statusVariant}
      statusLabel={statusLabel}
      progressPercent={progressPercent}
      progressEndDate={progressEndDate}
      currentLimit={currentLimit}
      targetLimit={targetLimit}
      chartData={warmupChartData}
      chartMaxValue={chartMaxValue}
      onLearnMore={handleLearnMore}
    />
  )
}
